import { Link, useNavigate } from '@tanstack/react-router';
import { Archive, FolderPlus, Search } from 'lucide-react';
import { useState, type ReactNode } from 'react';
import type { PoolRow } from '@nest-recruit/shared';
import { Button } from '../../../components/Button';
import { Chip } from '../../../components/Chip';
import { DataTable } from '../../../components/data-table/DataTable';
import { ACTIVE_POOL_TONE, ARCHIVED_TONE } from '../../../components/status-tones';
import type { Column, SortSpec } from '../../../components/data-table/types';
import { Popover, usePopover } from '../../../components/Popover';
import { Segmented } from '../../../components/Segmented';
import { ToneChip } from '../../../components/Tone';
import { EmptyState, ForbiddenState, ServerErrorState } from '../../../components/system-states/SystemState';
import { formatDate, formatNumber } from '../../../lib/format';
import { EMPTY_SELECTION, type SelectionState } from '../../../lib/selection';
import { useCanEdit, useCanView } from '../../../stores/session';
import { usePools } from '../api/pools-api';
import { PoolForm } from '../components/PoolPanels';
import styles from '../Pools.module.css';
import fit from '../../../components/FitPage.module.css';

/**
 * POOL-01 — "all pools with counts, owner, freshness (% of members contacted in last 6
 * months)" (E12). Archived pools are one switch away rather than gone: their people are
 * still members.
 */
export function PoolsPage(): ReactNode {
  const canView = useCanView('talent_pool');
  const canEdit = useCanEdit('talent_pool');
  const navigate = useNavigate();
  const [showArchived, setShowArchived] = useState<'live' | 'all'>('live');
  const pools = usePools(showArchived === 'all');
  const [sort, setSort] = useState<readonly SortSpec[]>([]);
  const [selection, setSelection] = useState<SelectionState>(EMPTY_SELECTION);
  const { anchor, triggerRef, toggle, close } = usePopover();

  if (!canView) return <ForbiddenState resource="talent pools" contact="Your admin" />;
  if (pools.error && !pools.data) return <ServerErrorState onRetry={() => void pools.refetch()} />;

  const rows = sortRows(pools.data ?? [], sort);
  const columns: readonly Column<PoolRow>[] = [
    {
      id: 'name',
      header: 'Pool',
      isIdentity: true,
      isSortable: true,
      defaultWidth: 280,
      cell: (row) => (
        <span className={styles.stack}>
          <Link to="/pools/$poolId" params={{ poolId: row.id }} className={styles.name}>
            {row.name}
          </Link>
          {row.description && <span className={styles.meta}>{row.description}</span>}
        </span>
      ),
      exportValue: (row) => row.name,
    },
    {
      id: 'state',
      header: 'Kind',
      defaultWidth: 130,
      cell: (row) =>
        row.isArchived ? (
          <ToneChip tone={ARCHIVED_TONE}>Archived</ToneChip>
        ) : row.isSystem ? (
          <Chip tone="progress" plain>
            System
          </Chip>
        ) : null,
      exportValue: (row) => kindLabel(row),
    },
    { id: 'owner', header: 'Owner', isSortable: true, defaultWidth: 170, cell: (row) => row.ownerName },
    { id: 'members', header: 'Members', isNumeric: true, isSortable: true, defaultWidth: 110, cell: (row) => formatNumber(row.memberCount) },
    {
      id: 'freshness',
      header: 'Contacted in 6 months',
      isNumeric: true,
      isSortable: true,
      defaultWidth: 170,
      cell: (row) => (row.freshnessPercent === null ? null : `${row.freshnessPercent}%`),
    },
    {
      id: 'expiring',
      header: 'Consent ending soon',
      isNumeric: true,
      isSortable: true,
      defaultWidth: 170,
      cell: (row) =>
        row.expiringSoonCount === 0 ? (
          '0'
        ) : (
          <Chip tone="warning" plain>
            {formatNumber(row.expiringSoonCount)} within 30 days
          </Chip>
        ),
      exportValue: (row) => (row.expiringSoonCount === 0 ? '0' : `${formatNumber(row.expiringSoonCount)} within 30 days`),
    },
    { id: 'created', header: 'Created', isSortable: true, defaultWidth: 120, cell: (row) => formatDate(row.createdAt) },
  ];

  return (
    <div className={`${styles.page} ${fit.fit}`}>
      <header className={styles.header}>
        <div className={styles.titleBlock}>
          <h1 className={styles.title}>Talent pools</h1>
          <p className={styles.lead}>People we did not hire but would call again. Pooling never changes how their application ended.</p>
        </div>
        <div className={styles.actions}>
          <Segmented
            label="Show"
            value={showArchived}
            options={[
              { value: 'live', label: 'In use' },
              { value: 'all', label: 'Include archived' },
            ]}
            onChange={setShowArchived}
          />
          <Button variant="outline" icon={Search} onClick={() => void navigate({ to: '/candidates' })}>
            Search candidates
          </Button>
          {canEdit && (
            <Button ref={triggerRef} variant="solid" icon={FolderPlus} onClick={toggle}>
              New pool
            </Button>
          )}
        </div>
      </header>

      <DataTable
        screenId="pool-library"
        label="Talent pools"
        columns={columns}
        rows={rows}
        toneOf={(row) => (row.isArchived ? ARCHIVED_TONE : ACTIVE_POOL_TONE)}
        getRowId={(row) => row.id}
        total={rows.length}
        sort={sort}
        onSortChange={setSort}
        selection={selection}
        onSelectionChange={setSelection}
        isSelectable={false}
        onOpenRow={(row) => void navigate({ to: '/pools/$poolId', params: { poolId: row.id } })}
        linkOf={(row) => `/pools/${row.id}`}
        exportName="talent-pools"
        isLoading={pools.isPending}
        emptyState={
          <EmptyState
            title="No pools yet"
            body="Make a pool for people you would like to call again — strong finalists, good CVs for a role that is not open yet."
            action={
              canEdit ? (
                <Button variant="solid" icon={FolderPlus} onClick={toggle}>
                  New pool
                </Button>
              ) : null
            }
          />
        }
      />
      {showArchived === 'all' && (
        <p className={styles.meta}>
          <Archive size={14} aria-hidden /> Archived pools leave every picker and the job match; restore one from its page.
        </p>
      )}

      <Popover anchor={anchor} onClose={close} align="right" width={380} label="New pool">
        <PoolForm
          pool={null}
          onCancel={close}
          onDone={(saved) => {
            close();
            void navigate({ to: '/pools/$poolId', params: { poolId: saved.id } });
          }}
        />
      </Popover>
    </div>
  );
}

function kindLabel(row: PoolRow): string | null {
  if (row.isArchived) return 'Archived';
  return row.isSystem ? 'System' : null;
}

const SORTERS: Readonly<Record<string, (row: PoolRow) => string | number>> = {
  name: (row) => row.name.toLowerCase(),
  owner: (row) => row.ownerName.toLowerCase(),
  members: (row) => row.memberCount,
  freshness: (row) => row.freshnessPercent ?? -1,
  expiring: (row) => row.expiringSoonCount,
  created: (row) => row.createdAt,
};

/** A short list sorted in the browser — every pool is already on screen. */
function sortRows(rows: readonly PoolRow[], sort: readonly SortSpec[]): readonly PoolRow[] {
  if (sort.length === 0) return rows;
  return [...rows].sort((a, b) => {
    for (const spec of sort) {
      const read = SORTERS[spec.columnId];
      if (!read) continue;
      const left = read(a);
      const right = read(b);
      if (left === right) continue;
      const order = left < right ? -1 : 1;
      return spec.direction === 'asc' ? order : -order;
    }
    return 0;
  });
}
